import GaugeChart from "react-gauge-chart";

const WITHIN_COLOR = "#22C55E";
const OVER_COLOR = "#EF4444";

const GaugeCard = ({
  label,
  value,
  minValue = 0,
  maxValue = 1,
  unit = "",
  description = "",
  threshold,
  higherIsBetter = false,
  withinLabel = "Within criteria",
  overLabel = "Over threshold",
  withinDiffWord = "within",
  overDiffWord = null,
}) => {
  const numValue = parseFloat(value) || 0;
  const numThreshold = parseFloat(threshold);
  const hasThreshold = !isNaN(numThreshold);

  const percent = Math.min(Math.max((numValue - minValue) / (maxValue - minValue), 0), 1);
  const thresholdPercent = hasThreshold
    ? Math.min(Math.max((numThreshold - minValue) / (maxValue - minValue), 0), 1)
    : 0.5;

  const isWithinLimit = hasThreshold
    ? (higherIsBetter ? numValue >= numThreshold : numValue <= numThreshold)
    : true;

  const statusColor = isWithinLimit ? WITHIN_COLOR : OVER_COLOR;
  const statusBg = isWithinLimit ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)";
  const statusLabel = isWithinLimit ? withinLabel : overLabel;

  // Arc order is left-to-right: for a "lower is better" metric the safe (green)
  // zone sits on the left, for a "higher is better" metric it sits on the right.
  const colors = hasThreshold
    ? (higherIsBetter ? [OVER_COLOR, WITHIN_COLOR] : [WITHIN_COLOR, OVER_COLOR])
    : higherIsBetter ? [OVER_COLOR, WITHIN_COLOR] : [WITHIN_COLOR, OVER_COLOR];
  const arcsLength = hasThreshold ? [thresholdPercent, 1 - thresholdPercent] : undefined;

  // For a semicircle gauge, percent 0 -> needle points left (-90deg), percent 0.5 -> up (0deg),
  // percent 1 -> right (+90deg). Pivot is approximated at the bottom-center of the chart box.
  const needleRotationDeg = (percent - 0.5) * 180;
  const thresholdRotationDeg = (thresholdPercent - 0.5) * 180;
  const needleColor = "#14B8A6";
  const needlePivotColor = "#6B7280";

  const diff = hasThreshold ? Math.abs(numValue - numThreshold) : null;
  const diffLabel = diff !== null ? (Number.isInteger(diff) ? diff : diff.toFixed(2)) : null;
  const diffText = hasThreshold
    ? `Threshold ${higherIsBetter ? "≥" : "≤"} ${numThreshold}${unit} · ${isWithinLimit
      ? `${withinDiffWord} by ${diffLabel}`
      : (overDiffWord ? `${overDiffWord} by ${diffLabel}` : `${diffLabel}${unit} over`)
    }`
    : null;

  return (
    <div
      className="iconFont card-section"
      style={{
        padding: 20,
        width: "100%",
        borderRadius: 8,
        border: `1px solid ${hasThreshold ? statusColor : "rgba(244,244,245,0.12)"}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <span style={{ color: "#F4F4F5" }}>{label}</span>
        {hasThreshold && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: statusBg,
              color: statusColor,
              border: `1px solid ${statusColor}`,
              borderRadius: 20,
              padding: "2px 10px",
              fontSize: 12,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ width: 10, height: 10, border: `1px solid ${statusColor}`, borderRadius: 2, display: "inline-block" }} />
            {statusLabel}
          </span>
        )}
      </div>

      <GaugeChart
        id={`gauge-chart-${label}`}
        style={{ position: "relative" }}
        nrOfLevels={colors.length}
        arcsLength={arcsLength}
        colors={colors}
        arcWidth={0.18}
        cornerRadius={0}
        percent={percent}
        arcPadding={0}
        textColor="#11305C"
        hideText={true}
        customNeedleStyle={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
        }}
        customNeedleComponent={
          <div style={{ position: "relative", width: "100%", height: "100%" }}>
            {hasThreshold && (
              // Wrapper reaches from the pivot (~15% up, matching the chart's default 5%
              // margin) to the arc's outer edge (~95% up); the 18%-tall child then lands
              // exactly on the arc band so the dash sits on the color boundary, not past it.
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  bottom: "15%",
                  width: 0,
                  height: "80%",
                  transformOrigin: "bottom center",
                  transform: `translateX(-50%) rotate(${thresholdRotationDeg}deg)`,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    width: 3,
                    height: "18%",
                    background: "#FFFFFF",
                    borderRadius: 1,
                    transform: "translateX(-50%)",
                  }}
                />
              </div>
            )}
            <div
              style={{
                position: "absolute",
                left: "50%",
                bottom: "15%",
                width: 3,
                height: "58%",
                background: needleColor,
                borderRadius: 2,
                transformOrigin: "bottom center",
                transform: `translateX(-50%) rotate(${needleRotationDeg}deg)`,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: "50%",
                bottom: "15%",
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: needlePivotColor,
                transform: "translate(-50%, 50%)",
              }}
            />
          </div>
        }
      />

      <div
        style={{
          textAlign: "center",
          fontSize: "1.3em",
          fontWeight: "bold",
          color: "white",
        }}
      >
        {value}
        {unit}
      </div>
      <div
        style={{
          textAlign: "center",
          color: "#ffffff",
          fontWeight: "bold",
          minHeight: "1.2em",
        }}
      >
        {description || " "}
      </div>

      {hasThreshold && (
        <div style={{ marginTop: 16 }}>
          <div style={{ position: "relative", height: 4, background: "rgba(244,244,245,0.15)", borderRadius: 2 }}>
            <div
              style={{
                position: "absolute",
                left: `${thresholdPercent * 100}%`,
                top: -3,
                width: 2,
                height: 10,
                background: "rgba(244,244,245,0.6)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: `calc(${percent * 100}% - 4px)`,
                top: -2,
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: statusColor,
              }}
            />
          </div>
          <div style={{ textAlign: "center", color: "rgba(255, 255, 255)", fontSize: 16, marginTop: 10 }}>
            {diffText}
          </div>
        </div>
      )}
    </div>
  );
};

export default GaugeCard;
